import {
  Component,
  Input,
  OnInit,
  forwardRef,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  AfterContentInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  NgZone,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectItemModel } from '../../models/select-item.model';
import { OptionComponent } from '../option/option.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  normalizePassiveListenerOptions,
  supportsPassiveEventListeners,
} from '@angular/cdk/platform';

@Component({
  selector: 'ca-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SelectComponent),
    },
  ],
  standalone: false,
})
export class SelectComponent
  implements OnInit, AfterViewInit, AfterContentInit, OnDestroy, ControlValueAccessor
{
  @ViewChild('select', { read: NgSelectComponent, static: false })
  select: NgSelectComponent;

  private _onScrollFn: () => void;

  @ContentChildren(OptionComponent) options: QueryList<OptionComponent>;

  @Output()
  change: EventEmitter<any> = new EventEmitter();

  @Input()
  class: string;

  @Input()
  style: string;

  @Input()
  optionStyle: string;

  @Input()
  appendTo: string = 'body';

  @Input()
  items: SelectItemModel[];

  @Input()
  disabled: boolean = false;

  @Input()
  placeholder: string;

  @Input()
  multiple: boolean = false;

  @Input()
  clearable: boolean = false;

  @Input()
  bringToFront: boolean = false;

  @Input()
  set tooltip(value: string) {
    this._tooltip = value;
    this._hasTooltip = true;
  }

  value: any;
  changes: MutationObserver;
  classList: string = '';

  get hasTooltip(): boolean {
    return this._hasTooltip;
  }

  get tooltip(): string {
    return this._tooltip;
  }

  private _hasTooltip: boolean = false;
  private _tooltip: string;

  constructor(private elementRef: ElementRef, protected ngZone: NgZone) {}

  writeValue(obj: any): void {
    obj = obj || obj == 0 ? obj : [];
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onChange(args: any) {}

  onTouched() {}

  onChanged(args: any): void {
    this.onChange(args);
    this.change.emit(args);
  }

  onBlurRequest(select: any) {
    if (this.hasTooltip) {
      select.blur();
    }
  }

  ngOnInit(): void {
    let eventOptions: AddEventListenerOptions | boolean;

    if (supportsPassiveEventListeners()) {
      //use the implementation on mozilla
      eventOptions = normalizePassiveListenerOptions({
        capture: true,
        passive: true,
      });
    } else {
      eventOptions = true;
    }

    this._onScrollFn = this.onScroll.bind(this);

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('scroll', this._onScrollFn, eventOptions);
    });
  }

  getItemLabel(itemId) {
    if (itemId > 0) {
      return this.items.find(f => f.data == itemId).label;
    }
  }

  ngAfterViewInit(): void {
    this.changes = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        this.classList = this.elementRef.nativeElement.classList;
      });
    });

    this.changes.observe(this.elementRef.nativeElement, {
      attributeFilter: ['class'],
    });
  }

  ngAfterContentInit(): void {
    this.processOptionComponents();
    this.options.changes.subscribe(() => {
      this.processOptionComponents();
    });
  }

  ngOnDestroy(): void {
    this.changes.disconnect();
    window.removeEventListener('scroll', this._onScrollFn, true);
  }

  private processOptionComponents() {
    var optionComponents = this.options.toArray();

    if (!this.items || this.items.length > 0) {
      this.items = [];
    }

    optionComponents.forEach(o => {
      if (!this.items.some(i => i.data === o.value)) {
        this.items.push({ data: o.value, label: o.label });
      }
    });
  }

  onOpen(eventArgs: any): void {
    setTimeout(() => {
      const dropdownId = this.select.dropdownId;
      const dropdownEl = document.getElementById(dropdownId);

      if (this.bringToFront) {
        dropdownEl.classList.add('above-all');
      }
    }, 1);
  }

  private onScroll(): void {
    if (this.select && this.select.isOpen) {
      this.ngZone.run(() => {
        this.select.dropdownPanel.adjustPosition();
      });
    }
  }
}
